/**
 * Application Insights SDK initialisation (singleton, idempotent)
 *
 * Creates and configures the App Insights instance. Pure singleton — no React dependency.
 * Safe to call multiple times; only initialises once.
 */

import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
let initialised = false;

export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

export function isAppInsightsInitialised(): boolean {
  return initialised;
}

export interface AppInsightsConfig {
  connectionString: string;
  correlationHeaderDomains?: string[];
  disableTelemetry?: boolean;
}

/**
 * Initialise Application Insights SDK.
 * Called once at app startup. Idempotent — safe to call multiple times.
 */
export function initialiseAppInsights(config: AppInsightsConfig): ApplicationInsights | null {
  if (initialised) return appInsights;

  const { connectionString } = config;

  if (!connectionString) {
    initialised = true;

    return null;
  }

  reactPlugin = new ReactPlugin();

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      correlationHeaderDomains: config.correlationHeaderDomains ?? [],
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableTelemetry: config.disableTelemetry ?? false,
      maxBatchInterval: 15000,
      maxBatchSizeInBytes: 102400,
      disableExceptionTracking: false,
      autoTrackPageVisitTime: true,
      enableDebug: import.meta.env.DEV,
    },
  });

  appInsights.loadAppInsights();
  initialised = true;

  return appInsights;
}
